/**
 * Featured Properties block — editor component.
 *
 * @package
 * @since   3.5.0
 */

import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	RangeControl,
	SelectControl,
	ToggleControl,
} from '@wordpress/components';

import BlockPreview from '../shared/BlockPreview';
import SectionHeaderPanel from '../shared/SectionHeaderPanel';

const LAYOUT_OPTIONS = [
	{ label: __( 'Grid', 'havenlytics' ), value: 'grid' },
	{ label: __( 'Carousel', 'havenlytics' ), value: 'carousel' },
];

const ORDERBY_OPTIONS = [
	{ label: __( 'Newest first', 'havenlytics' ), value: 'date' },
	{ label: __( 'Title (A–Z)', 'havenlytics' ), value: 'title' },
	{ label: __( 'Price: low to high', 'havenlytics' ), value: 'price_low' },
	{ label: __( 'Price: high to low', 'havenlytics' ), value: 'price_high' },
	{ label: __( 'Random', 'havenlytics' ), value: 'rand' },
];

export default function Edit( { attributes, setAttributes } ) {
	const { layout, columns, postsPerPage, orderby, visibleSlides, autoplay } =
		attributes;
	const blockProps = useBlockProps();
	// Saved "spotlight" blocks fall back to grid (option removed).
	const layoutValue = layout === 'spotlight' ? 'grid' : layout;
	const isCarousel = layoutValue === 'carousel';

	return (
		<>
			<InspectorControls>
				<SectionHeaderPanel
					attributes={ attributes }
					setAttributes={ setAttributes }
				/>

				<PanelBody
					title={ __( 'Layout', 'havenlytics' ) }
					initialOpen={ true }
				>
					<p className="components-base-control__help">
						{ __(
							'Featured listings reuse the Property Card Builder design.',
							'havenlytics'
						) }
					</p>
					<SelectControl
						label={ __( 'Layout style', 'havenlytics' ) }
						value={ layoutValue }
						options={ LAYOUT_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { layout: value } )
						}
						__nextHasNoMarginBottom
					/>
					{ layoutValue === 'grid' && (
						<RangeControl
							label={ __( 'Columns', 'havenlytics' ) }
							value={ columns }
							min={ 1 }
							max={ 4 }
							onChange={ ( value ) =>
								setAttributes( { columns: value } )
							}
							__nextHasNoMarginBottom
						/>
					) }
					{ isCarousel && (
						<>
							<RangeControl
								label={ __( 'Visible slides', 'havenlytics' ) }
								value={ visibleSlides }
								min={ 1 }
								max={ 5 }
								onChange={ ( value ) =>
									setAttributes( { visibleSlides: value } )
								}
								__nextHasNoMarginBottom
							/>
							<ToggleControl
								label={ __( 'Autoplay', 'havenlytics' ) }
								checked={ !! autoplay }
								onChange={ ( value ) =>
									setAttributes( { autoplay: value } )
								}
								__nextHasNoMarginBottom
							/>
						</>
					) }
				</PanelBody>

				<PanelBody
					title={ __( 'Query', 'havenlytics' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __( 'Number of properties', 'havenlytics' ) }
						value={ postsPerPage }
						min={ 1 }
						max={ 24 }
						onChange={ ( value ) =>
							setAttributes( { postsPerPage: value } )
						}
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Order by', 'havenlytics' ) }
						value={ orderby }
						options={ ORDERBY_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { orderby: value } )
						}
						__nextHasNoMarginBottom
					/>
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				<BlockPreview
					block="havenlytics/featured-properties"
					attributes={ attributes }
				/>
			</div>
		</>
	);
}
